// Settings › Switch (WI-17, WI-18): a bottom sheet over the app, like the other Settings sheets.
// ✕ and Back go back to Settings with nobody signed out; re-picking yourself does the same. Picking
// someone else syncs and switches, then closes every overlay so Settings doesn't reopen over the
// next person's app. A grown-up with a PIN gets the PIN pad in the sheet; a wrong PIN leaves the
// current person signed in. (The full-screen Who screen is only for a phone with nobody signed in.)
import { useEffect, useState } from 'react'
import type { Person } from '../../shared/events'
import { figureFor } from '../../shared/figures'
import { PinPad } from '../app/PinPad'
import { closeAllOverlays, closeOverlay } from '../app/router'
import { Sheet } from '../app/Sheet'
import { useMachineWords } from '../arcade/machines'
import { figureBg, FigureIcon } from '../art/FigureIcon'
import { HttpError, Offline } from '../data/api'
import { authError, useSession } from '../data/session'
import { t } from '../strings/en'
import { pinProblem } from './WhoScreen'

export function SwitchSheet() {
  const words = useMachineWords()
  const { people, loadPeople, switchTo, resident, person: me } = useSession()
  const [pinFor, setPinFor] = useState<Person | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const list = people ?? []
  const looks = new Map(list.map((p) => [p.id, figureFor(p, list)]))
  // Whose phone this is (WI-17): a grown-up picked on a child's phone is in parent mode.
  const residentChild = list.find((p) => p.kind === 'child' && p.id === (resident ?? (me?.kind === 'child' ? me.id : null)))
  const parentModeFor = (p: Person) => (p.kind !== 'child' && residentChild ? residentChild : null)

  useEffect(() => {
    loadPeople().catch(() => setError(t.who.offline))
  }, [loadPeople])

  const signIn = async (id: string, pin?: string) => {
    await switchTo(id, pin)
    closeAllOverlays()
  }

  const pick = async (p: Person) => {
    // Re-picking yourself: back to Settings, nothing signed out.
    if (me?.id === p.id) return closeOverlay()
    if (p.pin) return setPinFor(p)
    setBusy(p.id)
    setError(null)
    try {
      await signIn(p.id)
    } catch (e) {
      // Someone removed (or re-added) since this list was cached: refresh it.
      if (e instanceof HttpError && e.status === 404) void loadPeople().catch(() => undefined)
      setError(
        e instanceof Offline
          ? t.who.offline
          : e instanceof HttpError && e.status === 404
            ? t.who.gone
            : authError(e)?.error === 'set a PIN first'
              ? t.who.setPinFirst
              : String(e),
      )
    } finally {
      setBusy(null)
    }
  }

  if (pinFor) {
    // The PIN pad says what this pick means: "Dad — parent mode for 30 min on Lyam's phone".
    const child = parentModeFor(pinFor)
    const look = looks.get(pinFor.id) ?? figureFor(pinFor)
    return (
      <Sheet title={t.who.switchTitle}>
        <PinPad
          title={t.pin.hi(pinFor.name)}
          sub={child ? t.who.parentMode(pinFor.name, child.name) : t.pin.enter}
          head={
            <span className="who-pin-av" style={{ background: figureBg(look) }}>
              <FigureIcon figure={look} size={60} />
            </span>
          }
          onCancel={() => setPinFor(null)}
          onSubmit={async (pin) => {
            try {
              await signIn(pinFor.id, pin)
              return null
            } catch (e) {
              return pinProblem(e) ?? (e instanceof Offline ? t.who.offline : String(e))
            }
          }}
        />
      </Sheet>
    )
  }

  return (
    <Sheet title={t.who.switchTitle}>
      <div className="panel sheet-panel who-rows">
        {list.map((p) => {
          const look = looks.get(p.id)!
          const grown = p.kind !== 'child'
          const child = parentModeFor(p)
          return (
            <button
              key={p.id}
              className="row row-btn who-row press"
              onClick={() => void pick(p)}
              disabled={!!busy}
              aria-busy={busy === p.id}
            >
              <span className="av" style={{ background: figureBg(look) }}>
                <FigureIcon figure={look} size={32} />
              </span>
              <span className="who-row-text">
                <b>{p.name}</b>
                <small>{child ? t.who.parentModeLine(child.name) : p.pin ? t.who.parentLine : grown ? t.who.organiserLine : t.who.playerLine(words)}</small>
              </span>
              <span className={`tag ${grown ? 'mint' : 'blue'}`}>{grown ? t.who.organiser : t.who.player}</span>
            </button>
          )
        })}
      </div>
      {error ? (
        <p className="who-error" role="alert">
          {error}
        </p>
      ) : null}
    </Sheet>
  )
}
